import { monthLong, monthShort, weekdayShort, dateLocale } from './date_names.js';
import { startOfDay } from './date_read.js';

/*
 * A régua de datas das telas de viagem (#3990, #4124): "14 set", "Seg, 14 set",
 * "10–17 nov", "28 dez – 3 jan 2027". Recebe `Date` já lido no dia local (ver
 * `date_read.ts`).
 *
 * A regra do ano: sem `today`, nunca há ano — as telas da viagem e as listas
 * mostram o período sem ele. Com `today`, o ano entra por extenso no FIM, uma
 * vez só, quando alguma data do conjunto (`span`) cai fora do ano de `today`.
 * Parâmetro, e não `new Date()` interno, para o teste fixar o ano corrente.
 */

/** " 2027" (o ano de `printed`) quando alguma data de `span` cai fora do ano de
 *  `today`; vazio sem `today`. */
function yearSuffix(printed: Date, span: readonly Date[], today: Date | undefined): string {
  if (!today) return '';
  const year = today.getFullYear();
  return span.some((date) => date.getFullYear() !== year)
    ? ` ${String(printed.getFullYear())}`
    : '';
}

function bareDayMonth(date: Date, locale: string | null | undefined): string {
  return `${String(date.getDate())} ${monthShort(date, locale)}`;
}

/** "14 set" (ou "3 jan 2027" fora do ano de `today`). `span` é o conjunto que
 *  decide o ano: numa janela, a ponta final passa as duas pontas. */
export function formatDayMonth(
  date: Date,
  locale: string | null | undefined,
  today?: Date,
  span: readonly Date[] = [date],
): string {
  return `${bareDayMonth(date, locale)}${yearSuffix(date, span, today)}`;
}

/** "Seg, 14 set" — a vírgula é o léxico da peça. `today` e `span` como em
 *  `formatDayMonth`. */
export function formatWeekdayDayMonth(
  date: Date,
  locale: string | null | undefined,
  today?: Date,
  span: readonly Date[] = [date],
): string {
  return `${weekdayShort(date, locale)}, ${formatDayMonth(date, locale, today, span)}`;
}

/** Faixa compacta: "17 jun" (dia único ou sem fim), "20–22 jun" (mesmo mês),
 *  "29 jun – 2 jul" (meses diferentes); o ano, quando entra, só na última data
 *  ("28 dez – 3 jan 2027"). */
export function formatDateRange(
  start: Date,
  end: Date | null,
  locale: string | null | undefined,
  today?: Date,
): string {
  const last = end ?? start;
  if (startOfDay(start).getTime() === startOfDay(last).getTime()) {
    return formatDayMonth(start, locale, today);
  }
  const tail = formatDayMonth(last, locale, today, [start, last]);
  const yearGap = last.getFullYear() - start.getFullYear();
  if (yearGap === 0 && start.getMonth() === last.getMonth()) {
    return `${String(start.getDate())}–${tail}`;
  }
  // O ano da ponta inicial só fica implícito quando ela cai no ano anterior num
  // mês depois do fim ("28 dez – 3 jan 2027"). Fora disso ("10 nov 2026 – 17 nov
  // 2027") ele também vai por extenso, sempre que o fim levar o seu.
  const startImplied = yearGap === 0 || (yearGap === 1 && start.getMonth() > last.getMonth());
  const head =
    today && !startImplied
      ? formatDayMonth(start, locale, today, [start, last])
      : bareDayMonth(start, locale);
  return `${head} – ${tail}`;
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** "18:30" (ou "06:30 PM" em inglês): o relógio de parede, em tabela fixa como
 *  os nomes (sem `Intl`, que muda de saída entre motores). */
export function formatClockTime(date: Date, locale: string | null | undefined): string {
  const minutes = pad2(date.getMinutes());
  if (dateLocale(locale) !== 'en') return `${pad2(date.getHours())}:${minutes}`;
  const hour = date.getHours() % 12 || 12;
  return `${pad2(hour)}:${minutes} ${date.getHours() < 12 ? 'AM' : 'PM'}`;
}

/** "21 out, 10:00 – 24 out, 18:30": uma janela com hora (a locação do carro,
 *  na hora do balcão). O ano, quando entra, vai só na ponta final ("28 dez,
 *  10:00 – 3 jan 2027, 18:00"), a regra de `formatDateRange`. */
export function formatDateTimeWindow(
  start: Date,
  end: Date,
  locale: string | null | undefined,
  today?: Date,
): string {
  const head = `${formatDayMonth(start, locale)}, ${formatClockTime(start, locale)}`;
  const tail = `${formatDayMonth(end, locale, today, [start, end])}, ${formatClockTime(end, locale)}`;
  return `${head} – ${tail}`;
}

/** A legenda do mês na grade: "Setembro 2026" — inicial maiúscula e sem o "de"
 *  do português e do espanhol. */
export function formatMonthCaption(date: Date, locale: string | null | undefined): string {
  const month = monthLong(date, locale);
  return `${month.charAt(0).toLocaleUpperCase(dateLocale(locale))}${month.slice(1)} ${String(date.getFullYear())}`;
}

/** O nome acessível de um dia da grade: "15 de setembro" (pt, es) ou
 *  "September 15" (en). Sem dia da semana e sem ano (#3239 C6): a legenda já
 *  anuncia o mês e o ano, e o leitor de tela soletraria uma frase por célula. */
export function formatCalendarDay(date: Date, locale: string | null | undefined): string {
  const month = monthLong(date, locale);
  const day = String(date.getDate());
  return dateLocale(locale) === 'en' ? `${month} ${day}` : `${day} de ${month}`;
}
